import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import { useParams } from 'react-router-dom';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import { getPropertyOptions } from '../../../services/common';
import AddPropertyDialog from './AddPropertyDialog';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  Interested: {
    tint: '#1D9E75',
    pillBg: '#D6F5E8', pillColor: '#065F38', pillBorder: '#7DD3B0', dot: '#0F8A56',
  },
  Pending: {
    tint: '#EF9F27',
    pillBg: '#FEF3DA', pillColor: '#92600A', pillBorder: '#F8CE7A', dot: '#E59A1A',
  },
  'Not Interested': {
    tint: '#E24B4A',
    pillBg: '#FEECEC', pillColor: '#9B1C1C', pillBorder: '#F5A5A5', dot: '#E24B4A',
  },
  Suggested: {
    tint: '#9FE1CB',
    pillBg: '#E8FBF3', pillColor: '#0A7A49', pillBorder: '#A8DFCA', dot: '#1D9E75',
  },
  Default: {
    tint: '#B4B2A9',
    pillBg: 'rgba(0,0,0,0.04)', pillColor: '#5F5E5A', pillBorder: 'rgba(0,0,0,0.1)', dot: '#888780',
  },
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.Default;

// ─── Normalise API shape ──────────────────────────────────────────────────────
// API: id, name, developer { id, name, logo }, available_units, base_commission_rate, thumbnail, shared_on, status
const normalizeProperty = (p) => {
  const name = p.name || '—';
  const initials =
    (name || '—')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '—';

  // Prefer explicit thumbnail, then developer logo as fallback.
  const developerLogo = p.developer?.logo || null;
  const thumbnail = p.thumbnail || developerLogo || null;

  return {
    id: p.id,
    name,
    developer: p.developer?.name || '—',
    units: p.available_units != null ? `${p.available_units} units` : null,
    commission: p.base_commission_rate != null ? `${p.base_commission_rate}% commission` : null,
    sharedOn: p.shared_on || null,
    status: p.status || 'Suggested',
    initials,
    thumbnail,
  };
};

// ─── PropertyCard ─────────────────────────────────────────────────────────────

const PropertyCard = ({
  property,
  onRemove,
  onTriggerInterestCheck,
  interestCheckLoading,
  interestCheckSent,
}) => {
  const st = getStatusConfig(property.status);
  const tags = [property.units, property.commission].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '0.5px solid',
        borderColor: '#E5E7EB',
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: 'action.hover',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          '& .remove-btn': { opacity: 1 },
        },
      }}
    >
      {/* Top tint bar */}
      <Box sx={{ height: '4px', flexShrink: 0, bgcolor: st.tint }} />

      <Box sx={{ px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>

        {/* ── Top row: logo/initials + name + remove ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>

          {/* Developer logo / project thumbnail, fallback to initials badge */}
          {property.thumbnail ? (
            <Avatar
              src={property.thumbnail}
              variant="rounded"
              alt={property.name}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                flexShrink: 0,
                bgcolor: '#F0FBF6',
                border: '1px solid #C6EBD8',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                flexShrink: 0,
                bgcolor: '#F0FBF6',
                border: '1px solid #C6EBD8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.688rem',
                fontWeight: 700,
                color: '#0F7A4A',
                letterSpacing: '-0.3px',
              }}
            >
              {property.initials}
            </Box>
          )}

          {/* Name + developer */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {property.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, mt: '2px' }}>
              <PlaceIcon sx={{ fontSize: '0.75rem', color: '#A32D2D' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
                {property.developer}
              </Typography>
            </Box>
          </Box>

          {/* Remove button — fades in on card hover */}
          {onRemove && (
            <Tooltip title="Remove from shared properties">
              <IconButton
                size="small"
                className="remove-btn"
                onClick={() => onRemove(property.id)}
                sx={{
                  width: 24, height: 24, borderRadius: '5px',
                  color: 'text.disabled',
                  opacity: 0,
                  transition: 'opacity 0.15s, background 0.12s, color 0.12s',
                  '&:hover': { bgcolor: '#FCEBEB', color: '#A32D2D' },
                }}
              >
                <CloseIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* ── Tags ── */}
        {tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {tags.map((tag) => (
              <Box
                key={tag}
                component="span"
                sx={{
                  fontSize: '0.656rem',
                  fontWeight: 500,
                  px: 1,
                  py: '2px',
                  borderRadius: '20px',
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                  border: '0.5px solid',
                  borderColor: '#E5E7EB',
                }}
              >
                {tag}
              </Box>
            ))}
          </Box>
        )}

        {/* ── Footer ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 0.75,
            pt: 1.25,
            mt: 'auto',
            borderTop: '0.5px solid',
            borderColor: '#E5E7EB',
          }}
        >
          {/* Shared date */}
          <Typography sx={{ fontSize: '0.656rem', color: 'text.disabled' }}>
            {property.sharedOn ? `Shared ${property.sharedOn}` : 'Shared —'}
          </Typography>

          {/* Right side: interest check + status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {onTriggerInterestCheck && (
              <Tooltip
                title={
                  interestCheckSent
                    ? 'Interest check already sent on WhatsApp'
                    : 'Send WhatsApp interest check to lead'
                }
              >
                <Box component="span">
                  <Button
                    size="small"
                    onClick={onTriggerInterestCheck}
                    disabled={interestCheckLoading || interestCheckSent}
                    sx={{
                      fontSize: '0.656rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      color: interestCheckSent ? 'text.disabled' : 'text.secondary',
                      border: '0.5px solid',
                      borderColor: interestCheckSent ? 'divider' : 'action.selected',
                      bgcolor: 'transparent',
                      minWidth: 0,
                      px: 1.125,
                      py: '3px',
                      borderRadius: '20px',
                      '&:hover': {
                        bgcolor: interestCheckSent ? 'transparent' : 'action.hover',
                      },
                      '&.Mui-disabled': {
                        color: 'text.disabled',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    {interestCheckLoading
                      ? '…'
                      : interestCheckSent
                      ? 'Sent ✓'
                      : 'Trigger interest check'}
                  </Button>
                </Box>
              </Tooltip>
            )}

            {/* Status pill */}
            <Box
              component="span"
              sx={{
                fontSize: '0.656rem',
                fontWeight: 600,
                px: 1.125,
                py: '3px',
                borderRadius: '20px',
                bgcolor: st.pillBg,
                color: st.pillColor,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                border: `0.5px solid ${st.pillBorder}`,
              }}
            >
              <Box
                component="span"
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: st.dot, flexShrink: 0 }}
              />
              {property.status}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── LeadPropertiesShared ─────────────────────────────────────────────────────

const LeadPropertiesShared = ({
  properties: propsProperties = [],
  lead,
  onPropertiesUpdate,
}) => {
  const { id: leadId } = useParams();
  const {
    properties: contextProperties = [],
    loading,
    updateLeadProperties,
    fetchLeadProperties,
    fetchLeadDetails,
    removeLeadProperties,
    getPropertyInterestCheckStatus,
    triggerPropertyInterestCheck,
    getReEngagementStatus,
    triggerReEngagement,
  } = useLeadDetails();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableProperties, setAvailableProperties] = useState(null);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [interestCheckLoading, setInterestCheckLoading] = useState(false);
  const [interestCheckSent, setInterestCheckSent] = useState(false);
  const [reEngagementLoading, setReEngagementLoading] = useState(false);
  const [reEngagementSent, setReEngagementSent] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [propertyToRemove, setPropertyToRemove] = useState(null);

  const properties = propsProperties?.length > 0 ? propsProperties : contextProperties;
  const normalizedList = useMemo(
    () => (Array.isArray(properties) ? properties : []).map(normalizeProperty),
    [properties]
  );

  // Fetch statuses on mount
  useEffect(() => {
    if (!leadId || normalizedList.length === 0) return;
    let cancelled = false;
    getPropertyInterestCheckStatus(leadId)
      .then((data) => { if (!cancelled && data?.already_sent) setInterestCheckSent(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leadId, normalizedList.length, getPropertyInterestCheckStatus]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getReEngagementStatus(leadId)
      .then((data) => { if (!cancelled && data?.already_sent) setReEngagementSent(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leadId, getReEngagementStatus]);

  // ── Handlers ──

  const handleShareProperty = async () => {
    setDialogOpen(true);
    if (!availableProperties) {
      try {
        const data = await getPropertyOptions();
        setAvailableProperties(data);
      } catch (err) {
        console.error('Error fetching property options:', err);
      }
    }
  };

  const handleAddProperties = async (projectIds) => {
    if (!leadId || !projectIds?.length) return;
    setAdding(true);
    try {
      await updateLeadProperties(leadId, { property_ids: projectIds });
      await fetchLeadProperties(leadId);
      await fetchLeadDetails(leadId);
      onPropertiesUpdate?.();
      setDialogOpen(false);
    } catch (err) {
      console.error('Error adding properties:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveProperty = async (propertyId) => {
    if (!leadId || !propertyId) return;
    setRemovingId(propertyId);
    try {
      await removeLeadProperties(leadId, [propertyId]);
      await fetchLeadProperties(leadId);
      onPropertiesUpdate?.();
    } catch (err) {
      console.error('Error removing property:', err);
    } finally {
      setRemovingId(null);
      setRemoveDialogOpen(false);
      setPropertyToRemove(null);
    }
  };

  const handleTriggerInterestCheck = async () => {
    if (!leadId) return;
    setInterestCheckLoading(true);
    try {
      await triggerPropertyInterestCheck(leadId);
      setInterestCheckSent(true);
      onPropertiesUpdate?.();
    } catch (err) {
      console.error('Error triggering interest check:', err);
    } finally {
      setInterestCheckLoading(false);
    }
  };

  const handleTriggerReEngagement = async () => {
    if (!leadId) return;
    setReEngagementLoading(true);
    try {
      await triggerReEngagement(leadId);
      setReEngagementSent(true);
      onPropertiesUpdate?.();
    } catch (err) {
      console.error('Error triggering re-engagement:', err);
    } finally {
      setReEngagementLoading(false);
    }
  };

  const isLoading = loading?.properties;

  // ── Render ──

  return (
    <Box sx={{ px: 2, py: 2 }}>

      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left: title + count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '0.688rem',
              fontWeight: 600,
              color: 'text.disabled',
              textTransform: 'uppercase',
              letterSpacing: '0.7px',
            }}
          >
            Shared Properties
          </Typography>
          {normalizedList.length > 0 && (
            <Chip
              label={normalizedList.length}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.656rem',
                fontWeight: 600,
                bgcolor: '#ECFEF5',
                color: '#047857',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* Right: re-engagement + share */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
          <Tooltip
            title={
              reEngagementSent
                ? 'Re-engagement message already sent on WhatsApp'
                : 'Send re-engagement message to lead on WhatsApp'
            }
          >
            <Box component="span">
              <Button
                size="small"
                onClick={handleTriggerReEngagement}
                disabled={!leadId || reEngagementLoading || reEngagementSent}
                sx={{
                  fontSize: '0.719rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  color: reEngagementSent ? 'text.disabled' : 'text.secondary',
                  border: '0.5px solid',
                  borderColor: reEngagementSent ? 'divider' : 'action.selected',
                  bgcolor: 'transparent',
                  px: 1.5,
                  py: '6px',
                  borderRadius: '7px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: reEngagementSent ? 'transparent' : 'action.hover',
                  },
                  '&.Mui-disabled': { color: 'text.disabled', borderColor: 'divider' },
                }}
              >
                {reEngagementLoading
                  ? '…'
                  : reEngagementSent
                  ? 'Message sent ✓'
                  : 'Trigger re-engagement on WhatsApp'}
              </Button>
            </Box>
          </Tooltip>

          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '0.875rem !important' }} />}
            onClick={handleShareProperty}
            disabled={!leadId}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'none',
              bgcolor: '#1D9E75',
              color: 'white',
              px: 1.75,
              py: '6px',
              borderRadius: '7px',
              border: 'none',
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#0F6E56' },
              '&.Mui-disabled': { bgcolor: '#1D9E75', opacity: 0.45, color: 'white' },
            }}
          >
            Share Property
          </Button>
        </Box>
      </Box>

      {/* ── Loading ── */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={26} sx={{ color: '#1D9E75' }} />
        </Box>
      )}

      {/* ── Empty state ── */}
      {!isLoading && normalizedList.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ fontSize: '1.75rem', opacity: 0.2, mb: 1 }}>🏠</Typography>
          <Typography sx={{ fontSize: '0.781rem', color: 'text.disabled' }}>
            No properties shared yet
          </Typography>
        </Box>
      )}

      {/* ── Cards grid ── */}
      {!isLoading && normalizedList.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 1.25,
            alignItems: 'stretch',
          }}
        >
          {normalizedList.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onRemove={
                removingId === property.id
                  ? undefined
                  : () => {
                      setPropertyToRemove(property.id);
                      setRemoveDialogOpen(true);
                    }
              }
              onTriggerInterestCheck={handleTriggerInterestCheck}
              interestCheckLoading={interestCheckLoading}
              interestCheckSent={interestCheckSent}
            />
          ))}
        </Box>
      )}

      {/* ── Add property dialog ── */}
      <AddPropertyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAddProperties}
        availableProperties={availableProperties}
        sharedPropertyIds={normalizedList.map((p) => p.id)}
      />

      {/* Remove confirmation dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => {
          setRemoveDialogOpen(false);
          setPropertyToRemove(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          Remove shared property?
        </DialogTitle>
        <DialogContent sx={{ pb: 1.5 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            This will remove the property from this lead&apos;s shared list. The project itself
            will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 1.75, gap: 1 }}>
          <Button
            onClick={() => {
              setRemoveDialogOpen(false);
              setPropertyToRemove(null);
            }}
            size="small"
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              borderRadius: '6px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => propertyToRemove && handleRemoveProperty(propertyToRemove)}
            size="small"
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              borderRadius: '6px',
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadPropertiesShared;
